/**
 * PATH: packages/document-editor/src/reportExtensions.ts
 * FILE_ID: DOCEDITOR-REPORT-EXTENSIONS-001
 * PURPOSE: Render-safe TipTap nodes for report pagination and governed facts.
 * WHY: Report semantics must survive JSON and HTML without executable markup.
 * RELATED: contracts.ts, serialization.ts
 */
import { Node, mergeAttributes, type NodeConfig } from '@tiptap/core'
import { DEFAULT_A4_PAGE_SETTINGS } from './contracts'

const dataName = (key: string) => `data-${key.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)}`

const textAttr = (key: string, defaultValue = '') => ({
  default: defaultValue,
  parseHTML: (element: HTMLElement) => element.getAttribute(dataName(key)) ?? defaultValue,
})

const safeAttrs = (attrs: Record<string, unknown>, keys: string[]) =>
  Object.fromEntries(keys.flatMap((key) => attrs[key] == null ? [] : [[dataName(key), String(attrs[key])]]))

export const PageBreak = Node.create({
  name: 'pageBreak',
  group: 'block',
  atom: true,
  parseHTML: () => [{ tag: 'div[data-qdoc-node="page-break"]' }],
  renderHTML: ({ HTMLAttributes }) => ['div', mergeAttributes(HTMLAttributes, {
    'data-qdoc-node': 'page-break',
    class: 'qdoc-page-break',
  })],
})

export const SectionBreak = Node.create({
  name: 'sectionBreak',
  group: 'block',
  atom: true,
  addAttributes: () => ({
    sectionId: textAttr('sectionId'),
    kind: textAttr('kind', 'nextPage'),
  }),
  parseHTML: () => [{ tag: 'div[data-qdoc-node="section-break"]' }],
  renderHTML: ({ node }) => ['div', {
    'data-qdoc-node': 'section-break',
    ...safeAttrs(node.attrs, ['sectionId', 'kind']),
  }],
})

export const PageSettingsNode = Node.create({
  name: 'pageSettings',
  group: 'block',
  atom: true,
  addAttributes: () => ({
    size: textAttr('size', 'A4'),
    orientation: textAttr('orientation', 'portrait'),
    marginTopMm: textAttr('marginTopMm', String(DEFAULT_A4_PAGE_SETTINGS.marginsMm.top)),
    marginRightMm: textAttr('marginRightMm', String(DEFAULT_A4_PAGE_SETTINGS.marginsMm.right)),
    marginBottomMm: textAttr('marginBottomMm', String(DEFAULT_A4_PAGE_SETTINGS.marginsMm.bottom)),
    marginLeftMm: textAttr('marginLeftMm', String(DEFAULT_A4_PAGE_SETTINGS.marginsMm.left)),
  }),
  parseHTML: () => [{ tag: 'div[data-qdoc-node="page-settings"]' }],
  renderHTML: ({ node }) => ['div', {
    'data-qdoc-node': 'page-settings',
    ...safeAttrs(node.attrs, [
      'size', 'orientation', 'marginTopMm', 'marginRightMm', 'marginBottomMm', 'marginLeftMm',
    ]),
  }],
})

function governedBlock(
  name: string,
  htmlName: string,
  attributeDefaults: Record<string, unknown>,
): NodeConfig {
  const keys = Object.keys(attributeDefaults)
  return {
    name,
    group: 'block',
    content: 'block+',
    defining: true,
    addAttributes: () => Object.fromEntries(keys.map((key) => [
      key,
      textAttr(key, String(attributeDefaults[key])),
    ])),
    parseHTML: () => [{ tag: `section[data-qdoc-node="${htmlName}"]` }],
    renderHTML: ({ node }) => ['section', {
      'data-qdoc-node': htmlName,
      ...safeAttrs(node.attrs, keys),
    }, 0],
  }
}

export const HeaderBlock = Node.create(governedBlock(
  'headerBlock',
  'header',
  { scope: 'document', sectionId: '' },
))

export const FooterBlock = Node.create(governedBlock(
  'footerBlock',
  'footer',
  { scope: 'document', sectionId: '' },
))

export const GeneratedFactBlock = Node.create(governedBlock(
  'generatedFactBlock',
  'generated-fact',
  { factId: '', source: '', generatedAt: '', status: 'generated' },
))

export const AuditedOverride = Node.create(governedBlock(
  'auditedOverride',
  'audited-override',
  {
    factId: '',
    reason: '',
    overriddenBy: '',
    overriddenAt: '',
    originalValueHash: '',
    originalValue: '',
    currentValue: '',
  },
))

export const MergeField = Node.create({
  name: 'mergeField',
  group: 'inline',
  inline: true,
  atom: true,
  addAttributes: () => ({
    key: textAttr('key'),
    label: textAttr('label'),
    format: textAttr('format'),
    fallback: textAttr('fallback'),
  }),
  parseHTML: () => [{ tag: 'span[data-qdoc-node="merge-field"]' }],
  renderHTML: ({ node }) => ['span', {
    'data-qdoc-node': 'merge-field',
    ...safeAttrs(node.attrs, ['key', 'label', 'format', 'fallback']),
    contenteditable: 'false',
  }, `{{${String(node.attrs.label || node.attrs.key)}}}`],
})

export const REPORT_EXTENSIONS = [
  PageBreak,
  SectionBreak,
  PageSettingsNode,
  HeaderBlock,
  FooterBlock,
  GeneratedFactBlock,
  AuditedOverride,
  MergeField,
]
